import mustache from "mustache";
import { ClienteFincaBaseResponse } from "../../../../../modulos/terceros/response/ClienteFincaBaseResponse";
import { ClienteFincaLoteResponse } from "../../../../../modulos/terceros/response/ClienteFincaLoteResponse";
import {
  cargaAnimalPorHa,
  diasDescanso,
  diasEnFinca,
  diasOcupacion,
  pesoPromedioEstimado,
  pesoPromedioReal,
  proyectarPesoEstimado,
} from "../../../../../utils/ganaderiaUtils";
import { _miles } from "../../../../../utils/numberUtils";
import { IConnectRender } from "../../../../definitions/IConnectRender";
import template from "./ganaderiaBottomSheetTemplate.html";

mustache.parse(template);

/** Item (label + valor) del bottom-sheet. */
type SheetItem = { label: string; value: string };
/** Item del template (con flag de celda vacía = spacer del grid). */
type SheetTemplateItem = SheetItem & { empty: boolean };

/** Contador para ids únicos por instancia. */
let SHEET_ID = 0;

/** Detalle de un potrero (ganadería) como bottom-sheet. Render/Connect: arma las
 *  secciones Potrero / Lote activo y, en `connect()`, cablea el botón "Editar". */
export class GanaderiaBottomSheetRender implements IConnectRender {
  /** Id único de la instancia (para los ids del DOM). */
  private readonly _sheetId: number;

  /**
   * @param _lote        Lote (potrero) a mostrar.
   * @param _finca       Finca del lote (para los óptimos de ocupación/descanso).
   * @param _onEdit      Acción del botón "Editar".
   * @param _onRpv       Acción del botón "Pesar / Rotar / Veterinaria" (el botón
   *                     solo se muestra si se provee).
   * @param _onHistorico Acción del botón "Histórico" (el botón solo se muestra
   *                     si se provee).
   */
  constructor(
    private readonly _lote: ClienteFincaLoteResponse,
    private readonly _finca: ClienteFincaBaseResponse,
    private readonly _onEdit: () => void,
    private readonly _onRpv?: () => void,
    private readonly _onHistorico?: () => void,
  ) {
    SHEET_ID += 1;
    this._sheetId = SHEET_ID;
  }

  /** HTML del sheet (header + secciones). */
  render(): string {
    return mustache.render(template, {
      id: this._sheetId,
      nombreLote: this._lote.nombreLote,
      // Pesar/Rotar/Veterinaria: solo con animales en el potrero.
      withRpv: !!this._onRpv && this._lote.ganaderiaCantidadAnimales > 0,
      // Histórico: solo si el potrero tuvo algún movimiento (un potrero vacío
      // tiene pasado; uno que nunca recibió ni entregó animales, no).
      withHistorico: !!this._onHistorico && this._tieneMovimientos(),
      potrero: this._toTemplateItems(this._potreroItems()),
      loteActivo: this._toTemplateItems(this._loteActivoItems()),
    });
  }

  /** El potrero tuvo movimientos: tiene animales o alguna fecha de ingreso o
   *  salida (el server las setea en cualquier compra/venta/rotación). */
  private _tieneMovimientos(): boolean {
    return (
      this._lote.ganaderiaCantidadAnimales > 0 ||
      this._lote.ganaderiaUltimoIngreso != null ||
      this._lote.ganaderiaUltimaSalida != null
    );
  }

  /** Cablea los botones "Editar", "Pesar / Rotar / Veterinaria" e "Histórico". */
  connect(): void {
    this._editBtn = document.getElementById(`gbs-edit-${this._sheetId}`);
    this._editBtn?.addEventListener("click", this._onEdit);
    if (this._onRpv) {
      this._rpvBtn = document.getElementById(`gbs-rpv-${this._sheetId}`);
      this._rpvBtn?.addEventListener("click", this._onRpv);
    }
    if (this._onHistorico) {
      this._historicoBtn = document.getElementById(
        `gbs-historico-${this._sheetId}`,
      );
      this._historicoBtn?.addEventListener("click", this._onHistorico);
    }
  }

  /** Libera los listeners de los botones. */
  dispose(): void {
    this._editBtn?.removeEventListener("click", this._onEdit);
    this._editBtn = null;
    if (this._onRpv) {
      this._rpvBtn?.removeEventListener("click", this._onRpv);
    }
    this._rpvBtn = null;
    if (this._onHistorico) {
      this._historicoBtn?.removeEventListener("click", this._onHistorico);
    }
    this._historicoBtn = null;
  }

  /** Botón "Editar" (disponible tras `connect`). */
  private _editBtn: HTMLElement | null = null;
  /** Botón "Pesar / Rotar / Veterinaria" (disponible tras `connect`, opcional). */
  private _rpvBtn: HTMLElement | null = null;
  /** Botón "Histórico" (disponible tras `connect`, opcional). */
  private _historicoBtn: HTMLElement | null = null;

  /** Marca cada item con `empty` (celda vacía = spacer) para el template. */
  private _toTemplateItems(items: SheetItem[]): SheetTemplateItem[] {
    return items.map((item) => ({
      ...item,
      empty: item.label === "" && item.value === "",
    }));
  }

  /** Sección "Potrero": área + carga animal + días de ocupación/descanso vs óptimo. */
  private _potreroItems(): SheetItem[] {
    const lote = this._lote;
    const ocupado = lote.ganaderiaCantidadAnimales > 0;
    const carga = cargaAnimalPorHa(lote);
    const dias = ocupado ? diasOcupacion(lote) : diasDescanso(lote);
    const optimo = ocupado
      ? this._finca.ganaderiaDiasOcupacionOptimo
      : this._finca.ganaderiaDiasDescansoOptimo;

    return [
      { label: "Área", value: `${lote.areaHectareas.toFixed(2)} ha` },
      { label: "Animales/ha", value: carga > 0 ? _miles(carga) : "—" },
      {
        label: ocupado ? "Días ocupación" : "Días descanso",
        value: this._dias(dias),
      },
      {
        label: ocupado ? "Días ocupación óptimo" : "Días descanso óptimo",
        value: optimo > 0 ? `${optimo} días` : "—",
      },
    ];
  }

  /** Sección "Lote activo": animales, días en finca y peso. */
  private _loteActivoItems(): SheetItem[] {
    const lote = this._lote;
    const animales = lote.ganaderiaCantidadAnimales;
    const real = pesoPromedioReal(lote);
    const estimado = pesoPromedioEstimado(lote);

    let pesoPromedio = "—";
    let extraLabel = "";
    let extraValue = "";

    if (real != null) {
      pesoPromedio = `${_miles(real)} kg`;
      extraLabel = "Último pesaje";
      extraValue = new Date(
        lote.ganaderiaFechaUltimoPesaje!,
      ).toLocaleDateString("es-CO");
    } else if (estimado != null) {
      pesoPromedio = `${_miles(estimado)} kg*`;
      extraLabel = "Peso estimado";
      extraValue = `${_miles(proyectarPesoEstimado(lote))} kg`;
    }

    return [
      { label: "Animales", value: animales > 0 ? _miles(animales) : "—" },
      { label: "Días en finca", value: this._dias(diasEnFinca(lote)) },
      { label: "Peso promedio", value: pesoPromedio },
      { label: extraLabel, value: extraValue },
    ];
  }

  /** Formatea un conteo de días (`null` → "—"). */
  private _dias(dias: number | null): string {
    return dias != null ? `${dias} días` : "—";
  }
}
